import React, {Fragment, useState, useMemo} from 'react';
import {
    Flex,
    Image,
    Text,
    Grid,
    GridItem,
    Button,
    Icon,
    Link,
} from '@spothero/ui';
import IconCheck from '@spothero/icons/checkmark';
import FormatUtils from 'utils/format';
import ReservationUtils from 'utils/reservation-utils';
import {PROPTYPE_RESERVATION} from '@/proptypes/reservation-prop-types';

const OVERSIZE_SHORT_DESCRIPTION = 'Oversize Fee';

const PaymentDetails = ({reservation}) => {
    const {
        priceBreakdown,
        discountAmount,
        spotheroCreditUsed,
        paymentStatus,
        rateTitle,
        reservationType,
        facility: {oversizeSupported, licensePlateRequired},
        partner,
    } = reservation;
    const {totalPaid, totalPossibleRefund, totalPrice, items} = priceBreakdown;
    const formatPrice = price => FormatUtils.price(price).displayPrice;
    const isMonthly =
        reservationType === ReservationUtils.RESERVATION_TYPES.MONTHLY;
    const showMoreLess =
        !isMonthly || Boolean(discountAmount) || Boolean(spotheroCreditUsed);
    const [isOpen, setIsOpen] = useState(false);
    const toggleOpen = () => setIsOpen(prev => !prev);
    const partnerRestriction = partner?.updateVehicleEnabled === false;

    const oversizePaid = useMemo(
        () =>
            items.find(item => {
                return item.shortDescription === OVERSIZE_SHORT_DESCRIPTION;
            }),
        [items]
    );
    const oversizePossible = oversizeSupported && !oversizePaid;
    const itemsWithDiscount = items.filter(item => item.isPromocodeEligible);
    const itemsWithoutDiscount = items.filter(
        item => !item.isPromocodeEligible
    );

    return (
        <Grid padding={4} gridTemplateColumns="24px 1fr" gridGap={2}>
            <GridItem colSpan="1">
                <Image
                    src="/img/parking-pass/money.svg"
                    width={6}
                    height="auto"
                    marginRight={2.5}
                    color="gray.dark"
                />
            </GridItem>
            <GridItem colStart="2" colSpan="1" justifyContent="space-between">
                <Grid
                    gridTemplateColumns="2fr 1fr"
                    gridGap={isOpen ? 2 : 0}
                    sx={{
                        '@media print': {
                            gridGap: 2,
                        },
                    }}
                    transition="grid-gap 150ms ease-out"
                >
                    <GridItem
                        colStart="1"
                        colSpan="2"
                        justifyContent="space-between"
                        transition="all 250ms ease-in-out"
                        borderColor="gray.medium"
                        data-testid="paymentdetails-accordion"
                        maxHeight={isOpen ? 80 : 0}
                        borderBottomWidth={isOpen ? '1px' : 0}
                        paddingBottom={isOpen ? 2 : 0}
                        overflow={isOpen ? 'auto' : 'hidden'}
                        sx={{
                            '@media print': {
                                maxHeight: 'none',
                                borderBottomWidth: '1px',
                                paddingBottom: 2,
                                overflow: 'auto',
                            },
                        }}
                    >
                        <Grid gridTemplateColumns="repeat(2, 1fr)" gridGap={2}>
                            {itemsWithDiscount.map(item => {
                                return (
                                    <Fragment key={item.price}>
                                        <GridItem colSpan="1">
                                            <Text>{item.shortDescription}</Text>
                                        </GridItem>
                                        <GridItem colSpan="1" textAlign="right">
                                            <Text>
                                                ${formatPrice(item.price)}
                                            </Text>
                                        </GridItem>
                                    </Fragment>
                                );
                            })}
                            {Boolean(discountAmount) && (
                                <>
                                    <GridItem colSpan="1">
                                        <Text>Promo Code</Text>
                                    </GridItem>
                                    <GridItem colSpan="1" textAlign="right">
                                        <Text>
                                            &ndash;$
                                            {formatPrice(discountAmount)}
                                        </Text>
                                    </GridItem>
                                </>
                            )}
                            {itemsWithoutDiscount.map(item => {
                                return (
                                    <Fragment key={item.price}>
                                        <GridItem colSpan="1">
                                            <Text>{item.shortDescription}</Text>
                                        </GridItem>
                                        <GridItem colSpan="1" textAlign="right">
                                            <Text>
                                                ${formatPrice(item.price)}
                                            </Text>
                                        </GridItem>
                                    </Fragment>
                                );
                            })}
                            {Boolean(spotheroCreditUsed) && (
                                <>
                                    <GridItem colSpan="1">
                                        <Text>SpotHero Credit</Text>
                                    </GridItem>
                                    <GridItem colSpan="1" textAlign="right">
                                        <Text>
                                            &ndash;$
                                            {formatPrice(spotheroCreditUsed)}
                                        </Text>
                                    </GridItem>
                                </>
                            )}
                        </Grid>
                    </GridItem>

                    <GridItem colSpan="1">
                        <Text
                            sx={{
                                '@media print': {
                                    fontWeight: 'semibold',
                                },
                            }}
                            fontWeight={isOpen ? 'semibold' : 'normal'}
                        >
                            Total Price
                        </Text>
                    </GridItem>
                    <GridItem
                        colSpan="1"
                        textAlign="right"
                        data-testid="PaymentDetails-text-price"
                        display="flex"
                        gap={2}
                        alignItems="center"
                        justifyContent="flex-end"
                    >
                        {!isOpen && totalPrice !== totalPaid && (
                            <Text
                                textDecoration="line-through"
                                color="text.secondary.light"
                            >
                                ${formatPrice(totalPrice)}
                            </Text>
                        )}
                        <Text
                            sx={{
                                '@media print': {
                                    fontWeight: 'semibold',
                                },
                            }}
                            fontWeight={isOpen ? 'semibold' : 'normal'}
                        >
                            ${formatPrice(totalPaid)}
                        </Text>
                    </GridItem>
                </Grid>
            </GridItem>
            {paymentStatus === ReservationUtils.PAYMENT_STATUSES.REFUND && (
                <GridItem
                    colStart="2"
                    colSpan="1"
                    display="flex"
                    justifyContent="space-between"
                >
                    <Flex>
                        <Text fontWeight="semibold" color="success">
                            Refunded
                        </Text>
                    </Flex>
                    <Flex>
                        <Text fontWeight="semibold" color="success">
                            ${formatPrice(totalPossibleRefund)}
                        </Text>
                    </Flex>
                </GridItem>
            )}
            {oversizePaid && !isMonthly && (
                <GridItem colSpan="1" display="flex" justifyContent="center">
                    <Icon
                        width={4}
                        height="auto"
                        as={IconCheck}
                        color="success"
                    />
                </GridItem>
            )}
            <GridItem
                colStart="2"
                colSpan="1"
                display="flex"
                justifyContent="space-between"
                alignItems="start"
            >
                <Flex>
                    {oversizePaid && !isMonthly && (
                        <Text fontWeight="semibold" color="success">
                            Oversize Fee Paid
                        </Text>
                    )}
                    {oversizePossible && !isMonthly && !partnerRestriction && (
                        <Text fontSize="sm" color="gray.dark" paddingRight="16">
                            This facility may charge oversize fees upon arrival.
                        </Text>
                    )}
                    {isMonthly && (
                        <Text
                            fontSize="sm"
                            color="text.secondary.light"
                            lineHeight="1.4"
                            fontWeight="semibold"
                            paddingRight={4}
                        >
                            {rateTitle}
                        </Text>
                    )}
                </Flex>
                {showMoreLess && (
                    <Button
                        fontFamily="body"
                        variant="tertiary"
                        onClick={toggleOpen}
                        sx={{
                            '@media print': {
                                display: 'none',
                            },
                        }}
                    >
                        {isOpen ? 'Less' : 'More'}
                    </Button>
                )}
            </GridItem>
            {isMonthly && (oversizeSupported || licensePlateRequired) && (
                <GridItem
                    colStart="2"
                    colSpan="1"
                    display="flex"
                    justifyContent="space-between"
                    marginTop={1}
                >
                    <Text
                        fontSize="sm"
                        color="text.secondary.light"
                        lineHeight="1.4"
                        fontWeight="normal"
                        borderColor="gray.medium"
                        borderTopWidth="1px"
                        paddingTop={3}
                    >
                        Please make sure that your rate matches your vehicle
                        size or you may be charged an oversize fee at the
                        facility. If you&apos;re not sure please reach out to
                        our support at
                        <Link href="tel:1-844-243-7768">
                            {' (844) 243-7768'}
                        </Link>
                    </Text>
                </GridItem>
            )}
        </Grid>
    );
};

/* eslint-disable camelcase */
PaymentDetails.propTypes = {
    reservation: PROPTYPE_RESERVATION,
};

export default PaymentDetails;
